import { localArtUrl } from "../art";
import { useGame } from "../store";
import { OptionalImage } from "./OptionalImage";

const TONE_PATTERNS: Array<[string, RegExp]> = [
  ["boundary", /断界|界裂|界线|门缝|铭牌|窄桥|世界边缘|归灯/],
  ["candle", /逆命|烛芯|烛冠|烛灰|回烛|归烛|烛光|点烛/],
  ["rift", /裂命|裂光|无归|钟痕|钟声|骨骰|冷痕|破限|苍白徽记/],
  ["lamp", /灯火|引灯|小灯|灯芯|灯痕|旧路灯记|湿痕灯记|湿痕辨碑|湿页|旅途札记|三点灯痕|旧驿亭|夜雨旧驿|札记回灯/],
  ["relic", /界碑|碑阴|碑阴残刻|荒碑|拓片|旧盟|盟坛|暗号|木牌|火印|旧誓/],
  ["snow", /雪|冰|寒|峰|垭|冻/],
  ["sea", /海|港|船|潮|浪|岛|渔|码头/],
  ["fire", /火|熔|炉|烬|炎|岩浆|地火/],
  ["forest", /林|森|花|藤|猎|兽|蛇/],
  ["spirit", /鬼|魂|冥|阴|墓|邪|梦/],
  ["city", /市|城|坊|桥|楼|巷|驿|酒|商/],
];

function sceneTone(text: string) {
  return TONE_PATTERNS.find(([, pattern]) => pattern.test(text))?.[0] ?? "road";
}

function leadingStats(adjustments: Record<string, number>) {
  return Object.entries(adjustments)
    .filter(([, value]) => value !== 0)
    .sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]) || a[0].localeCompare(b[0], "zh-CN"))
    .slice(0, 4);
}

function rollPulseClass(
  rolled: { outcome: number; crit: "none" | "success" | "fail" } | undefined,
  revealed: boolean,
) {
  if (!rolled || !revealed) return null;
  if (rolled.crit === "success") return "pulse-crit-win";
  if (rolled.crit === "fail") return "pulse-crit-lose";
  return rolled.outcome >= 2 ? "pulse-win" : "pulse-lose";
}

const MILESTONE_WIN_ITEMS = new Set(["19721", "19724", "19727", "19730"]);
const MILESTONE_SCAR_ITEMS = new Set(["19722", "19725", "19728", "19731"]);
const MILESTONE_WIN_EVENTS = new Set(["20771", "20775", "20779", "20783", "28015", "28016", "28017"]);
const MILESTONE_SCAR_EVENTS = new Set(["20772", "20776", "20780", "20784"]);

function milestoneClass(eventId: string, grantedItemIds: string[]) {
  if (MILESTONE_WIN_EVENTS.has(eventId) || grantedItemIds.some((id) => MILESTONE_WIN_ITEMS.has(id))) {
    return "milestone-win";
  }
  if (MILESTONE_SCAR_EVENTS.has(eventId) || grantedItemIds.some((id) => MILESTONE_SCAR_ITEMS.has(id))) {
    return "milestone-scar";
  }
  return null;
}

function SceneFallback({ tone }: { tone: string }) {
  return (
    <div className={`scene-fallback fallback-${tone}`} aria-hidden="true">
      <span className="px-sky" />
      <span className="px-horizon" />
      <span className="px-ground" />
      <span className="px-landmark a" />
      <span className="px-landmark b" />
      <span className="px-path" />
    </div>
  );
}

export function SceneStage() {
  const view = useGame((s) => s.view);
  const rollRevealed = useGame((s) => s.rollRevealed);
  if (!view) return null;

  const imageUrl = view.event.imageUrl ?? localArtUrl(`event:${view.event.id}`);
  const tone = sceneTone(`${view.event.name} ${view.event.des}`);
  const stats = leadingStats(view.player.adjustments);
  const milestone = milestoneClass(view.event.id, view.grantedItems.map((item) => item.id));
  const rollPulse = rollPulseClass(view.pending?.rolled, rollRevealed);
  const challengePulse = view.challenges.length
    ? view.challenges.some((challenge) => !challenge.passed)
      ? "pulse-challenge-lose"
      : "pulse-challenge-win"
    : null;
  const pulseClass = rollPulse ?? challengePulse ?? "pulse-idle";
  const challengeSummary = view.challenges.length
    ? {
        passed: view.challenges.every((challenge) => challenge.passed),
        names: view.challenges.map((challenge) => challenge.name).slice(0, 2).join(" / "),
        count: view.challenges.length,
      }
    : null;

  return (
    <section className={`scene-stage tone-${tone} ${pulseClass} ${milestone ?? ""}`} aria-label="当前场景">
      <div className="scene-frame">
        <OptionalImage
          className="scene-art"
          src={imageUrl}
          alt=""
          loading="eager"
          fallback={<SceneFallback tone={tone} />}
        />
        <div className="scene-overlay" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
        <div className="scene-result-flash" aria-hidden="true" />
        <div className="scene-result-burst" aria-hidden="true">
          {Array.from({ length: 14 }, (_, index) => (
            <span key={index} />
          ))}
        </div>
      </div>

      <div className="scene-copy">
        <span className="section-kicker">当前场景</span>
        <h2>{view.event.name}</h2>
        <p>{view.event.des}</p>
      </div>

      <div className="scene-growth" aria-label="旅程状态">
        <span>D{view.difficulty.level} · {view.difficulty.label}</span>
        <span>藏品 {view.player.items.length}</span>
        <span>气运 {view.player.luck ?? 0}</span>
        {stats.length ? (
          stats.map(([name, value]) => (
            <span key={name} className={value > 0 ? "gain" : "loss"}>
              {name} {value > 0 ? `+${value}` : value}
            </span>
          ))
        ) : (
          <span>修正 0</span>
        )}
      </div>

      {challengeSummary && (
        <div className={`scene-challenge-chip ${challengeSummary.passed ? "pass" : "fail"}`}>
          <b>{challengeSummary.passed ? "挑战应验" : "挑战受挫"}</b>
          <span>
            {challengeSummary.names}
            {challengeSummary.count > 2 ? ` 等 ${challengeSummary.count} 项` : ""}
          </span>
        </div>
      )}
    </section>
  );
}
